import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useImperativeHandle, useId, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useValidateFormRequiredProps, dsI18n, getCommonClassNameDefault, getCommonFormVariantDefault } from '@skatteetaten/ds-core-utils';
import { ChevronDownIcon } from '@skatteetaten/ds-icons';
import { ErrorMessage } from '../ErrorMessage/index.esm.js';
import { LabelWithHelp } from '../LabelWithHelp/index.esm.js';
import { SelectOption } from '../SelectOption/index.esm.js';
import '../Help/index.esm.js';
import '@skatteetaten/ds-buttons';
import './styles.css';


var styles = {
    "selectContainer": "Select-module_selectContainer__Axig2",
    "select": "Select-module_select__0FvNb",
    "select_large": "Select-module_select_large__mccdq",
    "selectIcon": "Select-module_selectIcon__mjyJU",
    "selectIcon_large": "Select-module_selectIcon_large__wACCv"
};

const Select = /*#__PURE__*/ forwardRef(({ id: externalId, className = getCommonClassNameDefault(), classNames, lang, 'data-testid': dataTestId, defaultValue, description, errorMessage, helpSvgPath, helpText, label, placeholder, titleHelpSvg, variant = getCommonFormVariantDefault(), value, autoComplete, disabled, form, name, required, hideLabel, hidePlaceholder, showRequiredMark, onBlur, onChange, onFocus, onHelpToggle, children }, ref)=>{
    useValidateFormRequiredProps({
        required,
        showRequiredMark
    });
    const { t } = useTranslation('Shared', {
        i18n: dsI18n
    });
    const selectRef = useRef(null);
    useImperativeHandle(ref, ()=>selectRef?.current);
    const errorId = `selectErrorId-${useId()}`;
    const generatedId = `selectId-${useId()}`;
    const selectId = externalId ?? generatedId;
    const isLarge = variant === 'large';
    const selectClassName = `${styles.select} ${isLarge ? styles.select_large : ''}`.trim();
    const selectIconClassName = `${styles.selectIcon} ${isLarge ? styles.selectIcon_large : ''}`.trim();
    const placeholderPaletteGraphite50 = 'var(--palette-graphite-50)';
    useLayoutEffect(()=>{
        changePlaceholderColor();
    // eslint-disable-next-line
    }, [
        value
    ]);
    const handleChange = (e)=>{
        changePlaceholderColor();
        onChange?.(e);
    };
    const changePlaceholderColor = ()=>{
        if (!hidePlaceholder) {
            const select = selectRef.current;
            if (select.value.length === 0) {
                select.style.color = placeholderPaletteGraphite50;
            } else {
                select.style.color = '';
            }
        }
    };
    return /*#__PURE__*/ jsxs("div", {
        lang: lang,
        className: `${className} ${classNames?.container ?? ''}`.trim(),
        children: [
            /*#__PURE__*/ jsx(LabelWithHelp, {
                classNames: classNames,
                htmlFor: selectId,
                hideLabel: hideLabel,
                showRequiredMark: showRequiredMark,
                description: description,
                helpSvgPath: helpSvgPath,
                helpText: helpText,
                titleHelpSvg: titleHelpSvg,
                onHelpToggle: onHelpToggle,
                children: label
            }),
            /*#__PURE__*/ jsxs("div", {
                className: `${styles.selectContainer} ${classNames?.selectContainer ?? ''}`.trim(),
                children: [
                    /*#__PURE__*/ jsxs("select", {
                        ref: selectRef,
                        id: selectId,
                        className: selectClassName,
                        "data-testid": dataTestId,
                        autoComplete: autoComplete,
                        disabled: disabled,
                        form: form,
                        name: name,
                        required: required,
                        value: value,
                        defaultValue: defaultValue,
                        "aria-describedby": errorMessage ? errorId : undefined,
                        "aria-invalid": !!errorMessage || undefined,
                        onBlur: onBlur,
                        onChange: handleChange,
                        onFocus: onFocus,
                        children: [
                            !hidePlaceholder && /*#__PURE__*/ jsx("option", {
                                value: '',
                                children: placeholder ?? t('shared.ChooseValue')
                            }),
                            children
                        ]
                    }),
                    /*#__PURE__*/ jsx(ChevronDownIcon, {
                        className: selectIconClassName
                    })
                ]
            }),
            /*#__PURE__*/ jsx(ErrorMessage, {
                id: errorId,
                showError: !!errorMessage,
                className: classNames?.errorMessage,
                children: errorMessage ?? ''
            })
        ]
    });
});
Select.displayName = 'Select';
Select.Option = SelectOption;
Select.Option.displayName = 'Select.Option';

export { Select };
